import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { finalize } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { readLocalizedText } from '../../shared/dynamic-form/form-schema.models';
import { DynamicApiService, DynamicFormSubmissionDetail } from './dynamic-api.service';

@Component({
  standalone: true,
  selector: 'ain-dynamic-form-submission-detail',
  imports: [
    DatePipe,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    ButtonModule,
    TableModule,
    TagModule,
    RouterLink
  ],
  template: `
    <ain-page
      [eyebrow]="t('dynamic.submissions')"
      [title]="submission()?.formName ?? t('dynamic.submissions.detail.title')"
      [description]="t('dynamic.submissions.detail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'dynamic.submissions.detail.info.title'"
        [descriptionKey]="'dynamic.submissions.detail.info.description'"
        [bulletKeys]="[
          'dynamic.submissions.detail.info.bullets.fields',
          'dynamic.submissions.detail.info.bullets.actions',
          'dynamic.submissions.detail.info.bullets.record'
        ]" />

      <a class="back-link" [routerLink]="backLink()">{{ t('common.back') }}</a>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.loadFailed')" [message]="error() ?? ''" />
      } @else if (submission(); as detail) {
        <header class="submission-header card">
          <div class="header-top">
            <p-tag [value]="statusLabel(detail.status)" [severity]="statusSeverity(detail.status)" />
            @if (detail.workflowProcessInstanceId) {
              <p-tag
                [value]="t('dynamic.submissions.detail.workflow')"
                severity="info"
                [title]="detail.workflowProcessInstanceId" />
            }
            @if (detail.recordId) {
              <p-tag [value]="t('dynamic.submissions.detail.hasRecord')" severity="success" />
            }
          </div>
          <div class="meta">
            <span>{{ t('columns.date') }}: <strong>{{ detail.submittedAtUtc | date:'medium' }}</strong></span>
            <span>{{ t('dynamic.publications') }}: <strong>{{ detail.publicationSlug }}</strong></span>
          </div>
          @if (detail.recordId && detail.dynamicEntityId) {
            <a
              pButton
              severity="secondary"
              [routerLink]="['/dynamic/entities', detail.dynamicEntityId, 'records']"
              [label]="t('dynamic.submissions.detail.viewRecord')"></a>
          }
        </header>

        <section class="card">
          <h3>{{ t('dynamic.submissions.detail.fieldsTitle') }}</h3>
          @if (detail.fields.length === 0) {
            <p class="empty">{{ t('dynamic.submissions.detail.noFields') }}</p>
          } @else {
            <p-table [value]="detail.fields" [paginator]="detail.fields.length > 10" [rows]="10">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('dynamic.submissions.detail.fieldLabel') }}</th>
                  <th>{{ t('dynamic.value') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ fieldLabel(row) }}</td>
                  <td>
                    @if (row.fileDisplay) {
                      <div class="file-display">
                        @if (isImageContentType(row.fileDisplay.contentType)) {
                          <img
                            class="file-thumb"
                            [src]="resolveFileDisplayUrl(row.fileDisplay.url)"
                            [alt]="row.fileDisplay.fileName" />
                        }
                        <a
                          [href]="resolveFileDisplayUrl(row.fileDisplay.url)"
                          target="_blank"
                          rel="noopener">
                          {{ row.fileDisplay.fileName }}
                        </a>
                      </div>
                    } @else if (row.htmlValue) {
                      <div class="rich-text-value" [innerHTML]="row.htmlValue"></div>
                    } @else {
                      {{ formatFieldValue(row) }}
                    }
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>

        @if (detail.actionResults.length > 0) {
          <section class="card">
            <h3>{{ t('dynamic.submissions.detail.actionsTitle') }}</h3>
            <p-table [value]="detail.actionResults">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('dynamic.submissions.detail.actionType') }}</th>
                  <th>{{ t('columns.status') }}</th>
                  <th>{{ t('columns.description') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ actionTypeLabel(row.type) }}</td>
                  <td>
                    <p-tag [value]="actionStatusLabel(row.status)" [severity]="actionStatusSeverity(row.status)" />
                  </td>
                  <td>{{ actionMessage(row) }}</td>
                </tr>
              </ng-template>
            </p-table>
          </section>
        }
      }
    </ain-page>
  `,
  styles: [`
    .back-link { display: inline-block; margin-bottom: 12px; }
    .card {
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      padding: 16px;
      margin: 16px 0;
    }
    .submission-header { display: grid; gap: 12px; }
    .file-display { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .file-thumb {
      width: 48px;
      height: 48px;
      object-fit: cover;
      border-radius: 6px;
      border: 1px solid #e2e8f0;
    }
    .header-top { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
    .meta { display: flex; flex-wrap: wrap; gap: 16px; color: #475569; }
    .empty { color: #64748b; margin: 0; }
  `]
})
export class DynamicFormSubmissionDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(DynamicApiService);
  private readonly localization = inject(LocalizationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly submission = signal<DynamicFormSubmissionDetail | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);

  readonly backLink = computed(() => {
    const formId = this.route.snapshot.paramMap.get('formId');
    return formId ? ['/dynamic/forms', formId] : ['/dynamic/forms'];
  });

  ngOnInit(): void {
    const submissionId = this.route.snapshot.paramMap.get('submissionId');
    if (!submissionId) {
      this.loading.set(false);
      this.error.set(this.t('dynamic.submissions.detail.notFound'));
      return;
    }

    this.api.getSubmission(submissionId).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.loading.set(false))
    ).subscribe({
      next: detail => this.submission.set(detail),
      error: (err: HttpErrorResponse) => {
        this.error.set(err.status === 404
          ? this.t('dynamic.submissions.detail.notFound')
          : this.t('common.loadFailed'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  fieldLabel(row: DynamicFormSubmissionDetail['fields'][number]): string {
    return readLocalizedText(row.label, this.localization.activeCulture()) || row.fieldKey;
  }

  formatFieldValue(row: DynamicFormSubmissionDetail['fields'][number]): string {
    if (row.fileDisplay) {
      return row.fileDisplay.fileName;
    }

    if (!row.value?.trim()) {
      return '—';
    }

    if (row.semanticType === 'boolean') {
      const normalized = row.value.trim().toLowerCase();
      if (normalized === 'yes' || normalized === 'true') {
        return this.t('common.yes');
      }
      if (normalized === 'no' || normalized === 'false') {
        return this.t('common.no');
      }
    }

    return row.value;
  }

  isImageContentType(contentType: string): boolean {
    return contentType.toLowerCase().startsWith('image/');
  }

  resolveFileDisplayUrl(url: string): string {
    if (/^https?:\/\//i.test(url)) {
      return url;
    }
    const base = environment.apiBaseUrl.replace(/\/$/, '');
    const path = url.startsWith('/') ? url : `/${url}`;
    return `${base}${path}`;
  }

  statusLabel(status: string): string {
    const key = `dynamic.submissions.status.${status.toLowerCase()}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  statusSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' | undefined {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'processing':
      case 'validated':
        return 'info';
      case 'received':
        return 'secondary';
      case 'failed':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  actionTypeLabel(type: string): string {
    const key = `dynamic.submissions.actionType.${type}`;
    const translated = this.t(key);
    return translated === key ? type : translated;
  }

  actionStatusLabel(status: string): string {
    const key = `dynamic.submissions.actionStatus.${status.toLowerCase()}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  actionStatusSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' | undefined {
    switch (status.toLowerCase()) {
      case 'ok':
        return 'success';
      case 'failed':
        return 'danger';
      case 'skipped':
        return 'warn';
      default:
        return 'secondary';
    }
  }

  actionMessage(row: DynamicFormSubmissionDetail['actionResults'][number]): string {
    const localized = readLocalizedText(row.message, this.localization.activeCulture());
    if (localized) {
      return localized;
    }
    if (row.error?.trim()) {
      return row.error;
    }
    return '—';
  }
}
